import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { AssistantService } from '@/cloud/services/assistant.service';
import { AssistantChatComponent } from '@/cloud/components/assistant/assistant-chat.component';
import { TranslationService } from '@/layout/service/translation.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { ServiceDeskOverviewComponent } from './service-desk-overview.component';

/** How often the overview refetches while the page is open, unless an answer is streaming. */
const REFRESH_MS = 60_000;

/**
 * The full-page service desk: a helpdesk overview of every tenant the user covers beside the cross-tenant assistant.
 * Reached from the main menu by anyone with service desk access. The layout hides the tenant switcher here (route
 * data `hideTenantSwitcher`), because the page spans every tenant rather than the selected one.
 */
@Component({
    selector: 'app-service-desk-chat',
    standalone: true,
    imports: [AssistantChatComponent, ServiceDeskOverviewComponent, EmptyStateComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (assistant.serviceDeskAvailable()) {
            <div class="sdc">
                <app-assistant-chat class="sdc-chat" layout="page" />
                <app-service-desk-overview class="sdc-overview" [pulse]="assistant.pulse()" [loading]="assistant.pulseLoading()" [now]="now()" (ask)="ask($event)" (refresh)="assistant.loadPulse()" />
            </div>
        } @else if (assistant.statusChecked()) {
            <app-empty-state icon="pi pi-lock" [title]="t()('serviceDeskPage.noAccess')" [description]="t()('serviceDeskPage.noAccessDetail')" [spacious]="true" />
        } @else {
            <div class="sdc-loading" role="status"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i>{{ t()('assistant.pulseLoading') }}</div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                /* Fill the window under the top bar; the layout pads 6rem above and 2rem below. */
                height: calc(100dvh - 7.5rem);
                min-height: 32rem;
                margin-bottom: -1rem;
            }
            .sdc {
                display: grid;
                grid-template-columns: minmax(0, 1fr) 21rem;
                gap: 1rem;
                height: 100%;
                min-height: 0;
            }
            .sdc-chat,
            .sdc-overview {
                min-height: 0;
            }
            .sdc-loading {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.6rem;
                height: 100%;
                color: var(--text-color-secondary);
            }
            /* Narrower windows: the overview sits above the chat and the page scrolls. */
            @media (max-width: 1200px) {
                :host {
                    height: auto;
                    margin-bottom: 0;
                }
                .sdc {
                    grid-template-columns: minmax(0, 1fr);
                    height: auto;
                }
                .sdc-overview {
                    order: -1;
                }
                .sdc-chat {
                    height: calc(100dvh - 8rem);
                    min-height: 30rem;
                }
            }
        `
    ]
})
export class ServiceDeskChat {
    readonly assistant = inject(AssistantService);
    readonly t = inject(TranslationService).t;
    private readonly chat = viewChild(AssistantChatComponent);
    readonly now = signal(Date.now());

    constructor() {
        this.assistant.enterServiceDeskPage();
        // Status can arrive after the page opens (deep link, sign-in); switch to the service desk once it does.
        effect(() => {
            if (this.assistant.serviceDeskAvailable() && this.assistant.scope() !== 'serviceDesk') untracked(() => this.assistant.setScope('serviceDesk'));
        });

        const clock = setInterval(() => this.now.set(Date.now()), 5_000);
        const refresh = setInterval(() => {
            if (!this.assistant.streaming() && document.visibilityState === 'visible') this.assistant.loadPulse();
        }, REFRESH_MS);
        inject(DestroyRef).onDestroy(() => {
            clearInterval(clock);
            clearInterval(refresh);
            this.assistant.leaveServiceDeskPage();
        });
    }

    ask(prompt: string): void {
        this.chat()?.ask(prompt);
    }

    /** ⌘K / Ctrl+K puts the cursor in the composer here instead of opening the drawer. */
    @HostListener('document:keydown', ['$event'])
    onKey(event: KeyboardEvent): void {
        if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            this.chat()?.focus();
        }
    }
}
